import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import eventEmitter, { EVENT_TYPES } from './ContextEvents';
import { clientService, partnerService } from '../../shared/utils/serviceProvider';
import { logAuditEvent, AuditAction, AuditLevel } from '../../shared/utils/auditLogger';
import i18n from '../../i18n';

const cl = (key, opts) => i18n.t(`serviceMessages.clients.${key}`, opts);

export const ClientsContext = createContext();

// Custom hook for using the ClientsContext
export const useClients = () => {
  const context = useContext(ClientsContext);
  if (context === undefined) {
    throw new Error(cl('hook_outside_provider'));
  }
  return context;
};

// Helper mapping functions between API(DB) schema and UI schema
const mapApiToClient = (data) => {
  return {
    ...data,
    // Map facturation_clients fields to UI schema
    name: data.company_name || data.name || data.company || '',
    company: data.company_name || data.company || data.name || '',
    // Ensure type field is always present and defaults to 'company' for clients
    type: data.client_type || data.type || 'company',
    partner_type: data.partner_type || 'client', // Ensure partner_type is set
    // Map fiscal IDs (ZATCA TRN / VAT)
    taxNumber: data.tax_number || data.vat_number || data.nif || '',
    vatNumber: (data.vat_number || data.tax_number || data.fiscal_id || data.fiscalId || data.nif || '').toString().trim(),
    fiscalId: (data.fiscal_id || data.fiscalId || data.vat_number || data.nif || '').toString().trim(),
    cr: (data.cr_number || data.rc_number || data.rc || data.commercial_registration || '').toString().trim(),
    unifiedNationalNumber: (data.unified_national_number || data.nis || data.nis_number || '').toString().trim(),
    nif: (data.fiscal_id || data.fiscalId || data.vat_number || data.nif || '').toString().trim(),
    nis: (data.unified_national_number || data.nis || data.nis_number || '').toString().trim(),
    // Map phone fields from facturation_clients format
    phone: data.phone1 || data.phone || '',
    phone2: data.phone2 || '',
    phone3: data.phone3 || '',
    // Map registration numbers
    registrationNumber: data.cr_number || data.rc_number || data.registration_number || data.rc || '',
    rc: data.cr_number || data.rc_number || data.rc || data.registration_number || '',
    // Normalize article
    article: (data.article || '').toString().trim(),
    // Preserve category field
    category: data.category || '',
    // CRITICAL: Ensure status field is mapped correctly with 'active' as default
    status: data.status || 'active',
  };
};

const mapApiToSupplier = (data) => {
  return {
    ...data,
    name: (data.name || data.company_name || data.company || '').toString().trim() || data.name,
    // Use customType if available, otherwise use type, default to 'company' if neither exists
    type: data.customType || (data.type && data.type !== 'supplier' ? data.type : 'company'),
    partner_type: 'supplier', // Ligne lue depuis facturation_fournisseurs
    taxNumber: data.tax_number || data.vat_number || data.nif || '',
    vatNumber: (data.vat_number || data.tax_number || data.fiscalId || data.fiscal_id || data.nif || '').toString().trim(),
    fiscalId: (data.fiscalId || data.fiscal_id || data.vat_number || data.nif || '').toString().trim(),
    cr: (data.cr_number || data.rc_number || data.rc || data.commercial_registration || '').toString().trim(),
    unifiedNationalNumber: (data.unified_national_number || data.nis || data.nis_number || '').toString().trim(),
    nif: (data.fiscalId || data.fiscal_id || data.vat_number || data.nif || '').toString().trim(),
    nis: (data.unified_national_number || data.nis || data.nis_number || '').toString().trim(),
    // Preserve phone fields (table SQL : phone1)
    phone: data.phone1 || data.phone || '',
    phone2: data.phone2 || '',
    phone3: data.phone3 || '',
    // Registration numbers (table SQL : rc_number)
    registrationNumber: data.cr_number || data.rc_number || data.registration_number || data.rc || '',
    rc: (data.cr_number || data.rc_number || data.rc || data.registration_number || '').toString().trim(),
    // Normalize article - CRITICAL: ensure article is preserved
    article: (data.article || '').toString().trim(),
    // Preserve category field
    category: data.category || '',
    // Debug
    _originalType: data.type // For debugging
  };
};

const toApiClient = (client) => ({
  ...client,
  partner_type: client.partner_type || 'client', // Ensure partner_type is included
  tax_number: (client.taxNumber || client.vatNumber || client.tax_number || '').toString().trim(),
  vat_number: (client.vatNumber || client.taxNumber || client.fiscalId || client.fiscal_id || '').toString().trim(),
  fiscal_id: (client.fiscalId || client.fiscal_id || client.vatNumber || '').toString().trim(),
  fiscalId: (client.fiscalId || client.fiscal_id || client.vatNumber || '').toString().trim(),
  cr_number: (client.cr || client.rc || client.registrationNumber || client.registration_number || '').toString().trim(),
  unified_national_number: (client.unifiedNationalNumber || client.nis || client.nis_number || '').toString().trim(),
  registration_number: (client.registrationNumber || client.cr || client.rc || client.registration_number || '').toString().trim(),
  rc: (client.cr || client.rc || client.registrationNumber || '').toString().trim(),
  article: (client.article || '').toString().trim(),
  // Preserve category field
  category: (client.category || '').toString().trim(),
  // CRITICAL: Ensure status field is included and defaults to 'active'
  status: client.status || 'active',
  // Explicitly include phone fields
  phone: (client.phone || '').toString().trim(),
  phone2: (client.phone2 || '').toString().trim(),
  phone3: (client.phone3 || '').toString().trim(),
});

const toApiSupplier = (supplier) => {
  const fiscal = (supplier.fiscalId || supplier.fiscal_id || supplier.vatNumber || supplier.vat_number || supplier.nif || '').toString().trim();
  const rcNum = (supplier.cr_number || supplier.cr || supplier.rc_number || supplier.rc || supplier.registrationNumber || supplier.registration_number || '').toString().trim();
  const unifiedNum = (supplier.unified_national_number || supplier.unifiedNationalNumber || supplier.nis_number || supplier.nis || '').toString().trim();
  // Corps attendu par POST/PUT /api/facturation/fournisseurs (identique facturation_clients côté fiscal)
  return {
    supplier_type: supplier.supplier_type || supplier.customType || 'company',
    status: supplier.status || 'active',
    company_name: (supplier.company_name || supplier.name || supplier.company || '').toString().trim(),
    contact_person: (supplier.contact_person || supplier.contact || '').toString().trim(),
    email: (supplier.email || '').toString().trim(),
    phone1: (supplier.phone1 || supplier.phone || '').toString().trim(),
    phone2: (supplier.phone2 || '').toString().trim(),
    phone3: (supplier.phone3 || '').toString().trim(),
    address: (supplier.address || '').toString().trim(),
    city: (supplier.city || '').toString().trim(),
    fiscal_id: fiscal,
    vat_number: fiscal,
    article: (supplier.article || '').toString().trim(),
    rc_number: rcNum,
    cr_number: rcNum,
    unified_national_number: unifiedNum,
    nis_number: unifiedNum,
    payment_terms: (supplier.payment_terms || '').toString().trim(),
    notes: (supplier.notes || '').toString().trim(),
    customType: supplier.customType,
  };
};

export const ClientsProvider = ({ children }) => {
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]); // Add suppliers state
  const [loading, setLoading] = useState(true);

  // Refresh data function (can be called externally)
  const refreshData = useCallback(async () => {
    // Create AbortController to handle request cancellation
    const abortController = new AbortController();
    
    setLoading(true);
    try {
      // Load clients and suppliers from dedicated endpoints
      const [allClients, allSuppliers] = await Promise.all([
        clientService.getAll({ signal: abortController.signal }),
        partnerService.getPartners({ type: 'supplier', signal: abortController.signal }).catch(() => [])
      ]);
      
      if (allClients && Array.isArray(allClients)) {
        const clientsData = allClients.filter(item =>
          !item.partner_type || item.partner_type === 'client'
        );
        setClients(clientsData.map(mapApiToClient));

        try {
          const suppliersDataRaw = Array.isArray(allSuppliers) ? allSuppliers : [];
          setSuppliers(suppliersDataRaw.map(mapApiToSupplier));
        } catch (error) {
          setSuppliers([]);
        }
      } else {
        setClients([]);
        setSuppliers([]);
      }
    } catch (error) {
      // Ignore abort errors
      if (error.name === 'AbortError') {
        console.log('Client/supplier refresh was cancelled');
        return;
      }
      console.error('Error refreshing data from database:', error);
      setClients([]);
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
    
    // Return cleanup function
    return () => {
      abortController.abort();
    };
  }, []);

  // Load clients and suppliers from database on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('🔄 ClientsContext: Loading clients from API...');
        const [allClients, allSuppliers] = await Promise.all([
          clientService.getAll(),
          partnerService.getPartners({ type: 'supplier' }).catch(() => [])
        ]);
        
        console.log('📦 ClientsContext: Raw API response:', allClients);
        
        if (allClients && Array.isArray(allClients)) {
          // Filter to only include records marked as clients (or legacy records without partner_type)
          const clientsData = allClients.filter(item => 
            !item.partner_type || item.partner_type === 'client'
          );
          const mappedClients = clientsData.map(mapApiToClient);
          console.log(`✅ ClientsContext: Loaded ${clientsData.length} clients:`, mappedClients);
          setClients(mappedClients);
        } else {
          setClients([]);
          console.log('⚠️ ClientsContext: No clients found in database');
        }
        
        // Load suppliers from database  
        try {
          const suppliersDataRaw = Array.isArray(allSuppliers) ? allSuppliers : [];
          const suppliersData = suppliersDataRaw;
          setSuppliers(suppliersData.map(mapApiToSupplier));
          console.log(`Loaded ${suppliersData.length} suppliers from database`);
        } catch (error) {
          console.log('Error loading suppliers:', error);
          setSuppliers([]);
        }
      } catch (error) {
        console.error('❌ ClientsContext: Error loading data from database:', error);
        setClients([]);
        setSuppliers([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Listen for refresh events from sidebar
  useEffect(() => {
    const handleRefresh = () => {
      console.log('Clients context received refresh event');
      refreshData();
    };

    // Listen for general refresh events
    window.addEventListener('refresh-clients', handleRefresh);
    window.addEventListener('refresh-all-data', handleRefresh);
    window.addEventListener('refresh-section', (e) => {
      if (e.detail?.section === 'clients') {
        handleRefresh();
      }
    });

    return () => {
      window.removeEventListener('refresh-clients', handleRefresh);
      window.removeEventListener('refresh-all-data', handleRefresh);
      window.removeEventListener('refresh-section', handleRefresh);
    };
  }, [refreshData]);

  // We don't need to save clients back to database on every change
  // as each operation will directly update the database
  // This removes the previous localStorage saving effects

  // Add a new client
  const addClient = useCallback(async (client) => {
    try {
      const newClient = {
        ...client,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        type: client.type || 'company', // Default to 'company' if no type specified
        partner_type: 'client', // Explicitly mark as client
        status: client.status || 'active'
      };

      // Add to database
      const result = await clientService.createClient(toApiClient(newClient));
      
      if (result) {
        // Update local state after database operation
        setClients(prev => {
          const updated = [...prev, newClient];
          console.log('Client added:', newClient);
          
          // Emit events for real-time updates
          eventEmitter.emit(EVENT_TYPES.CLIENT_CREATED, newClient);
          eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
          
          return updated;
        });
        
        // Log audit event
        logAuditEvent(
          'client_create',
          cl('audit_client', { name: newClient.name || newClient.company || '' }),
          cl('audit_create_client_detail', { id: newClient.id }),
          AuditLevel.SUCCESS
        );
        
        return newClient;
      } else {
        throw new Error(cl('add_failed'));
      }
    } catch (error) {
      console.error('Error adding client:', error);
      throw error;
    }
  }, []);

  // Update an existing client
  const updateClient = useCallback(async (id, updatedClient) => {
    try {
      const oldClient = clients.find(client => client.id === id);
      if (!oldClient) {
        throw new Error(cl('not_found', { id }));
      }
      
      // Update client in database
      // Ensure article and type are explicitly preserved
      const clientToUpdate = { 
        ...oldClient, 
        ...updatedClient, 
        id,
        // Force preserve critical fields even if they were missing in the update
        article: updatedClient.article || oldClient.article || '',
        type: (updatedClient.type && updatedClient.type.trim()) || oldClient.type || 'company', // Ensure valid client type
        partner_type: 'client', // Always ensure this is a client
        // CRITICAL: Explicitly preserve status field - don't override with default
        status: updatedClient.status !== undefined ? updatedClient.status : (oldClient.status || 'active')
      };
      console.log('Updating client with data:', clientToUpdate);
      const result = await clientService.updateClient(id, toApiClient(clientToUpdate));
      
      if (result) {
        // Update local state after database operation
        setClients(prev => {
          const updated = prev.map(client => client.id === id ? clientToUpdate : client);
          
          console.log('Client updated:', id, updatedClient);
          
          // Check if status changed for specific event
          if (oldClient.status !== updatedClient.status) {
            eventEmitter.emit(EVENT_TYPES.CLIENT_STATUS_CHANGED, {
              id,
              oldStatus: oldClient.status,
              newStatus: updatedClient.status
            });
          }
          
          // Emit general update event
          eventEmitter.emit(EVENT_TYPES.CLIENT_UPDATED, {
            id,
            oldClient,
            newClient: clientToUpdate
          });
          
          eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
          
          return updated;
        });
        
        // Log audit event
        logAuditEvent(
          'client_update',
          cl('audit_client', { name: clientToUpdate.name || clientToUpdate.company || '' }),
          cl('audit_update_client_detail', { id }),
          AuditLevel.INFO
        );
      } else {
        throw new Error(cl('update_failed'));
      }
    } catch (error) {
      console.error('Error updating client:', error);
      throw error;
    }
  }, [clients]);

  // Delete a client
  const deleteClient = useCallback(async (id) => {
    try {
      const clientToDelete = clients.find(client => client.id === id);
      if (!clientToDelete) {
        throw new Error(cl('not_found', { id }));
      }
      
      // Delete from database
      const result = await clientService.deleteClient(id);
      
      if (result) {
        // Update local state after database operation
        setClients(prev => {
          const updated = prev.filter(client => client.id !== id);
          
          console.log('Client deleted:', id);
          
          // Emit delete event
          eventEmitter.emit(EVENT_TYPES.CLIENT_DELETED, clientToDelete);
          eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
          
          return updated;
        });
        
        // Log audit event
        logAuditEvent(
          'client_delete',
          cl('audit_client', { name: clientToDelete.name || clientToDelete.company || '' }),
          cl('audit_delete_client_detail', { id }),
          AuditLevel.WARNING
        );
      } else {
        throw new Error(cl('delete_failed'));
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      throw error;
    }
  }, [clients]);

  // Add a new supplier
  const addSupplier = useCallback(async (supplier) => {
    try {
      // Preserve the UI-selected type (company, individual, etc.) in a separate field
      // while setting the database type to 'supplier'
      const originalType = supplier.type;
      
      const newSupplier = {
        ...supplier,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        customType: originalType, // Store the UI-selected type here
        type: originalType, // Keep the UI type in the frontend object
        partner_type: 'supplier', // Explicitly mark as supplier
        // CRITICAL: Always ensure article is explicitly set
        article: supplier.article || ''
      };

      console.log('Adding supplier with data:', newSupplier);
      console.log('Original type:', originalType);
      console.log('Article value before API call:', newSupplier.article);
      
      // Ensure type is always supplier in the API data
      const apiData = toApiSupplier(newSupplier);
      apiData.type = 'supplier'; // Force the database type to be 'supplier'
      apiData.customType = originalType; // Store UI type in customType
      apiData.article = newSupplier.article; // CRITICAL: Explicitly set article
      
      console.log('API data for supplier creation:', apiData);
      
      // Persist to database via partners service
      const result = await partnerService.createPartner(apiData, { type: 'supplier' });
      if (result) {
        // Make sure we preserve the UI type and article in the local state
        const supplierWithCorrectType = {
          ...result,
          type: originalType, // Keep the UI type for display
          customType: originalType,
          // CRITICAL: Force article value preservation
          article: supplier.article || result.article || '' 
        };
        
        // Debug article value
        console.log('Article value from API result:', result.article);
        console.log('Final article value in UI:', supplierWithCorrectType.article);
        
        setSuppliers(prev => {
          const updated = [...prev, supplierWithCorrectType];
          console.log('Supplier added with preserved type:', supplierWithCorrectType);
          
          // Trigger refresh to ensure data is properly loaded
          setTimeout(() => refreshData(), 500);
          
          // Emit events for real-time updates
          eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
          
          return updated;
        });
        
        // Log audit event
        logAuditEvent(
          'supplier_create',
          cl('audit_supplier', { name: supplierWithCorrectType.name || '' }),
          cl('audit_create_supplier_detail', { id: supplierWithCorrectType.id }),
          AuditLevel.SUCCESS
        );
        
        return supplierWithCorrectType;
      } else {
        throw new Error(cl('add_supplier_failed'));
      }
    } catch (error) {
      console.error('Error adding supplier:', error);
      throw error;
    }
  }, [refreshData]);

  // Update an existing supplier
  const updateSupplier = useCallback(async (id, updatedSupplier) => {
    try {
      const oldSupplier = suppliers.find(supplier => supplier.id === id);
      if (!oldSupplier) {
        throw new Error(cl('supplier_not_found', { id }));
      }
      
      // Preserve the UI-selected type (company, individual, etc.)
      const originalType = updatedSupplier.type || oldSupplier.type;
      
      console.log('Updating supplier - Original UI type:', originalType);
      console.log('Old supplier article:', oldSupplier.article);
      console.log('Updated supplier article:', updatedSupplier.article);
      
      // CRITICAL: Make sure we preserve the article value
      const preservedArticle = updatedSupplier.article || oldSupplier.article || '';
      console.log('Preserved article value:', preservedArticle);
      
      // Persist to database
      const supplierToUpdate = { 
        ...oldSupplier, 
        ...updatedSupplier,
        // Force preserve critical fields even if they were missing in the update
        article: preservedArticle, // Use the preserved article value
        // Keep the UI type in the frontend object for display
        type: originalType,
        partner_type: 'supplier', // Always ensure this is a supplier
        // Store the UI type in customType for database storage
        customType: originalType
      };
      
      console.log('Updating supplier with data:', supplierToUpdate);
      
      // Ensure type is always supplier in the API data
      const apiData = toApiSupplier(supplierToUpdate);
      apiData.type = 'supplier'; // Force the database type to be 'supplier'
      apiData.customType = originalType; // Store UI type in customType
      apiData.article = preservedArticle; // CRITICAL: Explicitly set article
      
      console.log('API data for supplier update:', apiData);
      
      const result = await partnerService.updatePartner(id, apiData, { type: 'supplier' });
      if (result) {
        // Debug article from result
        console.log('Article value in API result:', result.article);
        
        setSuppliers(prev => {
          const updated = prev.map(supplier => 
            supplier.id === id ? {
              ...supplierToUpdate,
              // Ensure these critical fields are preserved
              type: originalType, // Keep UI type for display
              article: preservedArticle // Use the preserved article
            } : supplier
          );
          
          console.log('Supplier updated with preserved type:', id, originalType);
          console.log('Final article value after update:', preservedArticle);
          
          // Trigger refresh to ensure data is properly loaded
          setTimeout(() => refreshData(), 500);
          
          // Emit general update event
          eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
          
          return updated;
        });
        
        // Log audit event
        logAuditEvent(
          'supplier_update',
          cl('audit_supplier', { name: supplierToUpdate.name || '' }),
          cl('audit_update_supplier_detail', { id }),
          AuditLevel.INFO
        );
      } else {
        throw new Error(cl('update_supplier_failed'));
      }
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw error;
    }
  }, [suppliers, refreshData]);

  // Delete a supplier
  const deleteSupplier = useCallback(async (id) => {
    try {
      const supplierToDelete = suppliers.find(supplier => supplier.id === id);
      if (!supplierToDelete) {
        throw new Error(cl('supplier_not_found', { id }));
      }
      
      // Persist deletion to database via partners service
      const result = await partnerService.deletePartner(id, { type: 'supplier' });
      if (result) {
        setSuppliers(prev => {
          const updated = prev.filter(supplier => supplier.id !== id);
          
          console.log('Supplier deleted:', id);
          
          // Emit delete event
          eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
          
          return updated;
        });
        
        // Log audit event
        logAuditEvent(
          'supplier_delete',
          cl('audit_supplier', { name: supplierToDelete.name || '' }),
          cl('audit_delete_supplier_detail', { id }),
          AuditLevel.WARNING
        );
      } else {
        throw new Error(cl('delete_supplier_failed'));
      }
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw error;
    }
  }, [suppliers]);

  // Get a client by ID
  const getClientById = useCallback((id) => {
    return clients.find(client => client.id === id);
  }, [clients]);

  // Get active clients only
  const getActiveClients = useCallback(() => {
    console.log('Filtering active clients, current status values:', clients.map(c => ({ id: c.id, name: c.name, status: c.status })));
    const isActiveLike = (s) => {
      const x = String(s ?? '').trim().toLowerCase();
      return x === '' || x === 'active' || x === 'actif' || x === '1';
    };
    return clients.filter((client) => isActiveLike(client.status));
  }, [clients]);

  // Get active suppliers only
  const getActiveSuppliers = useCallback(() => {
    console.log('Current suppliers state:', JSON.stringify(suppliers));
    console.log('Filtering active suppliers, current status values:', suppliers.map(s => s.status));
    const isActiveLike = (s) => {
      const x = String(s ?? '').trim().toLowerCase();
      return x === '' || x === 'active' || x === 'actif' || x === '1';
    };
    return suppliers.filter((supplier) => isActiveLike(supplier.status));
  }, [suppliers]);

  // Filter clients by various criteria
  const filterClients = useCallback((filters) => {
    return clients.filter(client => {
      let match = true;

      // Handle status filtering with support for both 'active'/'inactive' and 'actif'/'inactif'
      if (filters.status) {
        // Map between English and French status values
        const statusMap = {
          'active': ['active', 'actif'],
          'inactive': ['inactive', 'inactif'],
          'actif': ['active', 'actif'],
          'inactif': ['inactive', 'inactif']
        };
        
        // Check if client's status matches any of the equivalent statuses
        const equivalentStatuses = statusMap[filters.status] || [filters.status];
        if (!equivalentStatuses.includes(client.status)) {
          match = false;
        }
      }

      if (filters.type && client.type !== filters.type) {
        match = false;
      }

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const nameMatch = client.name && client.name.toLowerCase().includes(searchLower);
        const emailMatch = client.email && client.email.toLowerCase().includes(searchLower);
        const phoneMatch = client.phone && client.phone.toLowerCase().includes(searchLower);
        
        if (!(nameMatch || emailMatch || phoneMatch)) {
          match = false;
        }
      }

      return match;
    });
  }, [clients]);

  // Value object to be provided to consumers
  const value = {
    clients,
    suppliers,
    loading,
    addClient,
    updateClient,
    deleteClient,
    addSupplier,
    updateSupplier,
    deleteSupplier,
    getClientById,
    getActiveClients,
    getActiveSuppliers,
    filterClients,
    refreshData
  };

  return (
    <ClientsContext.Provider value={value}>
      {children}
    </ClientsContext.Provider>
  );
}; 

